import React from 'react';
import {useQuery} from '@tanstack/react-query';

import {propertiesQueryKey} from 'apps/experiments/src/react-query';
import {eventPropertyValuesClient} from 'common/report/queries/query-api/event-property-values-client';
import {EXPERIMENT_PROPERTY_PREFIX} from 'common/report/constants';

import {useExperimentPageContext} from '../context';
import {useExperimentSettings} from './experiment-settings';
import {useStagedExperiment} from './staged-experiment';

const CONTROL_VARIANT_MATCHER =
  /(^|\W)(control|disabled|baseline|original|default|placebo|reference|standard|current|existing|none|a|null|untreated|main|normal|old|0|contrôle|deshabilitado|désactivé|base|referencia|de base|por defecto|predeterminado|par défaut|estándar|actuel|existente|existant|ninguno|aucun|nulo|nul|sin tratar|no tratado|non traité|principal|viejo|ancien|cero|zéro|testigo|témoin|sin modificar|对照|禁用|基线|原始|默认|安慰剂|参考|标准|当前|现有|无|甲|空|未处理|主要|正常|旧|零|对照组|空白对照|対照|無効|ベースライン|元の|デフォルト|プラセボ|参照|標準|現在|既存|なし|ヌル|未処理|主|通常|古い|ゼロ|対照群|統制群|標準群)($|\W)/i;

export const useExperimentOptions = () => {
  const {projectId, workspaceId} = useExperimentPageContext();

  const {stagedExperiment} = useStagedExperiment();
  const experimentSettings = useExperimentSettings();

  const {data: experimentNames = []} = useQuery({
    queryKey: propertiesQueryKey({
      projectId: projectId!,
      workspaceId: workspaceId!,
      params: {
        eventName: experimentSettings.eventName,
        propertyName: experimentSettings.nameProperty?.value,
      },
    }),
    queryFn: () =>
      eventPropertyValuesClient.query({
        eventName: experimentSettings.eventName,
        ...(experimentSettings.nameProperty?.customPropertyId
          ? {customProperty: experimentSettings.nameProperty}
          : {propertyName: experimentSettings.nameProperty?.value}),
        projectId: projectId!,
        workspaceId: workspaceId!,
      }) as Promise<string[]>,
  });

  const experimentName = stagedExperiment?.settings?.exposureEventSettings?.experimentName;

  const {data: variantNames = []} = useQuery({
    queryKey: propertiesQueryKey({
      projectId: projectId!,
      workspaceId: workspaceId!,
      params: {
        propertyName: `${EXPERIMENT_PROPERTY_PREFIX}${experimentName}`,
      },
    }),
    enabled: !!experimentName,
    queryFn: () =>
      eventPropertyValuesClient.query({
        propertyName: `${EXPERIMENT_PROPERTY_PREFIX}${experimentName}`,
        projectId: projectId!,
        workspaceId: workspaceId!,
      }) as Promise<string[]>,
  });

  const defaultVariantName = React.useMemo(() => {
    if (!variantNames.length) {
      return undefined;
    }

    return variantNames.find((name) => CONTROL_VARIANT_MATCHER.test(name));
  }, [variantNames]);

  return {
    experimentNames,
    variantNames,
    defaultVariantName,
  };
};
